import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, ArrowLeft, MailCheck } from 'lucide-react';
import { authService } from '../services/api';
import { useSEO } from '../hooks/useSEO';

const ForgotPassword = () => {
  useSEO({
    title: 'Forgot Password',
    description: 'Request a password reset link for your Family Wishlist account.',
    keywords: 'forgot password, reset password, family wishlist',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      'name': 'Forgot Password - Family Wishlist',
      'description': 'Password recovery portal for Family Wishlist accounts.',
      'url': `${window.location.origin}/forgot-password`
    }
  });

  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);

    try {
      const res = await authService.forgotPassword({ email });
      setMessage(res.data.msg || 'If an account with that email exists, a password reset link has been sent.');
    } catch (err) {
      setError(err.response?.data?.msg || 'Failed to request password reset. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto mt-20 card">
      <Link to="/login" className="inline-flex items-center text-sm text-gray-600 hover:text-xmas-green mb-4">
        <ArrowLeft size={16} className="mr-1" /> Back to Sign In
      </Link>

      <h2 className="text-3xl font-bold text-center text-xmas-red mb-2">Forgot Password</h2>
      <p className="text-sm text-gray-600 text-center mb-6">
        Enter your registered email address below and we'll send you a link to reset your password.
      </p>

      {message ? (
        <div className="bg-green-50 border border-green-200 text-green-800 p-4 rounded-md text-center space-y-3">
          <MailCheck className="mx-auto text-xmas-green" size={40} />
          <p className="text-sm font-medium">{message}</p>
          <p className="text-xs text-gray-500">Check your inbox and spam folder for the email.</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Email Address</label>
            <input
              type="email"
              className="w-full p-2 border rounded mt-1"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
            />
          </div>

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-2 rounded text-sm">
              {error}
            </div>
          )}

          <button
            type="submit"
            className="btn-primary w-full flex justify-center items-center gap-2 disabled:opacity-50"
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className="animate-spin" size={20} />
                Sending Reset Link...
              </>
            ) : (
              'Send Reset Link'
            )}
          </button>
        </form>
      )}
    </div>
  );
};

export default ForgotPassword;
